import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Package,
  Search,
  Plus,
  Filter,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  MoreVertical,
  Edit,
  Eye,
  BarChart3,
} from 'lucide-react';
import api from '../services/api';
import { StockItem, PaginatedResponse, StockCategory, StockLocation } from '../types';
import Layout from '../components/layout/Layout';

export default function Stock() {
  const navigate = useNavigate();
  const [items, setItems] = useState<StockItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    total_pages: 0,
    limit: 20,
  });
  const [showFilters, setShowFilters] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [categories, setCategories] = useState<StockCategory[]>([]);
  const [locations, setLocations] = useState<StockLocation[]>([]);
  const [activeMenu, setActiveMenu] = useState<number | null>(null);
  const [lowStockItems, setLowStockItems] = useState<StockItem[]>([]);

  useEffect(() => {
    fetchOptions();
    fetchLowStockItems();
  }, []);

  useEffect(() => {
    fetchItems();
  }, [page, categoryFilter, locationFilter, lowStockOnly]);

  const fetchOptions = async () => {
    try {
      const [catRes, locRes] = await Promise.all([
        api.get('/stock/options/categories'),
        api.get('/stock/options/locations'),
      ]);
      if (catRes.data.success) setCategories(catRes.data.data);
      if (locRes.data.success) setLocations(locRes.data.data);
    } catch (error) {
      console.error('Failed to fetch options:', error);
    }
  };

  const fetchLowStockItems = async () => {
    try {
      const response = await api.get('/stock/stats/low-stock?limit=5');
      if (response.data.success) {
        setLowStockItems(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch low stock items:', error);
    }
  };

  const fetchItems = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        ...(search && { search }),
        ...(categoryFilter && { category_id: categoryFilter }),
        ...(locationFilter && { location_id: locationFilter }),
        ...(lowStockOnly && { low_stock: 'true' }),
      });

      const response = await api.get<PaginatedResponse<StockItem>>(`/stock?${params}`);
      if (response.data.success) {
        setItems(response.data.data);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Failed to fetch stock items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchItems();
  };

  const formatCurrency = (amount?: number) => {
    if (amount === undefined || amount === null) return '-';
    return `R ${amount.toFixed(2)}`;
  };

  const getStockLevelColor = (item: StockItem) => {
    if (!item.reorder_level) return 'text-dark-text';
    if (item.quantity_available <= 0) return 'text-status-red';
    if (item.quantity_available <= item.reorder_level) return 'text-status-yellow';
    return 'text-status-green';
  };

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Package className="text-primary-500" size={28} />
            <h1 className="text-2xl font-semibold">Stock Inventory</h1>
          </div>
          <button
            onClick={() => navigate('/stock/new')}
            className="btn btn-primary flex items-center gap-2"
          >
            <Plus size={20} />
            Add Item
          </button>
        </div>

        {/* Low Stock Alert */}
        {lowStockItems.length > 0 && (
          <div className="card bg-status-yellow/5 border-status-yellow/20 mb-6">
            <div className="flex items-center gap-3 mb-3">
              <AlertTriangle className="text-status-yellow" size={20} />
              <h3 className="font-medium text-status-yellow">Low Stock Alert</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {lowStockItems.map((item) => (
                <div
                  key={item.id}
                  onClick={() => navigate(`/stock/${item.id}`)}
                  className="bg-dark-surface rounded-lg p-3 cursor-pointer hover:bg-dark-elevated transition-colors"
                >
                  <p className="text-sm font-medium truncate">{item.name}</p>
                  <p className="text-xs text-dark-muted">{item.sku}</p>
                  <p className="text-lg font-bold text-status-yellow mt-1">
                    {item.quantity_available} left
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Search & Filters */}
        <div className="card mb-6">
          <div className="flex gap-4 items-center">
            <form onSubmit={handleSearch} className="flex gap-4 flex-1">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-dark-muted" size={20} />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by SKU, name, or description..."
                  className="input pl-10 w-full"
                />
              </div>
              <button type="submit" className="btn btn-secondary">
                Search
              </button>
            </form>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`btn ${showFilters ? 'btn-primary' : 'btn-secondary'} flex items-center gap-2`}
            >
              <Filter size={18} />
              Filters
              <ChevronDown size={16} className={`transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {showFilters && (
            <div className="flex gap-4 mt-4 pt-4 border-t border-dark-border flex-wrap">
              <div>
                <label className="block text-sm text-dark-muted mb-1">Category</label>
                <select
                  value={categoryFilter}
                  onChange={(e) => {
                    setCategoryFilter(e.target.value);
                    setPage(1);
                  }}
                  className="input"
                >
                  <option value="">All Categories</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-dark-muted mb-1">Location</label>
                <select
                  value={locationFilter}
                  onChange={(e) => {
                    setLocationFilter(e.target.value);
                    setPage(1);
                  }}
                  className="input"
                >
                  <option value="">All Locations</option>
                  {locations.map((loc) => (
                    <option key={loc.id} value={loc.id}>
                      {loc.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={lowStockOnly}
                    onChange={(e) => {
                      setLowStockOnly(e.target.checked);
                      setPage(1);
                    }}
                    className="rounded border-dark-border bg-dark-elevated"
                  />
                  <span className="text-sm">Low stock only</span>
                </label>
              </div>
              <button
                onClick={() => {
                  setCategoryFilter('');
                  setLocationFilter('');
                  setLowStockOnly(false);
                  setPage(1);
                }}
                className="btn btn-ghost self-end"
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>

        {/* Stock List */}
        <div className="card">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-dark-muted">
              <Package size={48} className="mx-auto mb-4 opacity-50" />
              <p>No stock items found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-dark-border">
                      <th className="text-left py-3 px-4 font-medium text-dark-muted">SKU</th>
                      <th className="text-left py-3 px-4 font-medium text-dark-muted">Name</th>
                      <th className="text-left py-3 px-4 font-medium text-dark-muted">Category</th>
                      <th className="text-left py-3 px-4 font-medium text-dark-muted">Location</th>
                      <th className="text-right py-3 px-4 font-medium text-dark-muted">On Hand</th>
                      <th className="text-right py-3 px-4 font-medium text-dark-muted">Available</th>
                      <th className="text-right py-3 px-4 font-medium text-dark-muted">Cost</th>
                      <th className="text-right py-3 px-4 font-medium text-dark-muted">Sell</th>
                      <th className="text-right py-3 px-4 font-medium text-dark-muted">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr
                        key={item.id}
                        className="border-b border-dark-border/50 hover:bg-dark-elevated/50 cursor-pointer transition-colors"
                        onClick={() => navigate(`/stock/${item.id}`)}
                      >
                        <td className="py-4 px-4 font-mono text-sm">{item.sku}</td>
                        <td className="py-4 px-4">
                          <div>
                            <p className="font-medium">{item.name}</p>
                            {item.is_serialized && (
                              <span className="text-xs text-primary-500">Serialized</span>
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-4 text-dark-muted">{item.category_name || '-'}</td>
                        <td className="py-4 px-4 text-dark-muted">{item.location_name || '-'}</td>
                        <td className="py-4 px-4 text-right">{item.quantity_on_hand}</td>
                        <td className={`py-4 px-4 text-right font-medium ${getStockLevelColor(item)}`}>
                          {item.quantity_available}
                          {item.reorder_level && item.quantity_available <= item.reorder_level && (
                            <AlertTriangle size={14} className="inline ml-1" />
                          )}
                        </td>
                        <td className="py-4 px-4 text-right text-dark-muted">
                          {formatCurrency(item.cost_price)}
                        </td>
                        <td className="py-4 px-4 text-right">{formatCurrency(item.sell_price)}</td>
                        <td className="py-4 px-4 text-right">
                          <div className="relative inline-block" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => setActiveMenu(activeMenu === item.id ? null : item.id)}
                              className="p-2 hover:bg-dark-elevated rounded-lg transition-colors"
                            >
                              <MoreVertical size={16} />
                            </button>
                            {activeMenu === item.id && (
                              <div className="absolute right-0 top-full mt-1 w-40 bg-dark-surface border border-dark-border rounded-lg shadow-xl z-10">
                                <button
                                  onClick={() => {
                                    navigate(`/stock/${item.id}`);
                                    setActiveMenu(null);
                                  }}
                                  className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-dark-elevated transition-colors"
                                >
                                  <Eye size={16} />
                                  View
                                </button>
                                <button
                                  onClick={() => {
                                    navigate(`/stock/${item.id}/edit`);
                                    setActiveMenu(null);
                                  }}
                                  className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-dark-elevated transition-colors"
                                >
                                  <Edit size={16} />
                                  Edit
                                </button>
                                <button
                                  onClick={() => {
                                    navigate(`/stock/${item.id}#serials`);
                                    setActiveMenu(null);
                                  }}
                                  className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-dark-elevated transition-colors"
                                >
                                  <BarChart3 size={16} />
                                  Serials
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.total_pages > 1 && (
                <div className="flex items-center justify-between pt-4 border-t border-dark-border mt-4">
                  <p className="text-sm text-dark-muted">
                    Showing {(page - 1) * pagination.limit + 1} to{' '}
                    {Math.min(page * pagination.limit, pagination.total)} of {pagination.total} items
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                      className="p-2 rounded-lg hover:bg-dark-elevated disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <span className="text-sm">
                      Page {page} of {pagination.total_pages}
                    </span>
                    <button
                      onClick={() => setPage(page + 1)}
                      disabled={page === pagination.total_pages}
                      className="p-2 rounded-lg hover:bg-dark-elevated disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronRight size={20} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
